import React, { useState, useEffect, memo } from 'react'
import isMobile from 'ismobilejs'
import qs from 'query-string'
import {
  Container,
  Css,
  ListContainer,
  Tabs,
  TextBody
} from '@pmi/pmi-iqos-ds-react'
import { canUseDOM } from '@pmi/pmi-iqos-shared-react'
import {
  Configure,
  Index,
  InstantSearch,
  SearchBox,
  useInstantSearch,
  useInfiniteHits
} from 'react-instantsearch'
import InstantsearchCss from 'instantsearch.css/themes/satellite.css'
import InstantSearchCSSOverride from './instantsearch-css-override'
import { mapResultsToTabsItems, IndexIdTypes, DisplayTypes } from './utils'
import {
  Article,
  ArticleCard,
  ProductCard,
  LoadMoreButton,
  NoResults,
  RecommendedSection
} from './ui'
import { DcxAnalytics } from '@dcx-common/services'

const InstantSearchComp = ({
  translations,
  searchClient,
  sourcesConfiguration,
  recommendedContent,
  initialQuerySuggestionsHits,
  spiceIdHash
}) => {
  let query = ''
  if (canUseDOM) {
    const params = qs.parse(window.location.search)
    query = params.query
  }

  const handleStateChange = ({ uiState, setUiState }) => {
    DcxAnalytics.searchInitiatedEvent(uiState['']?.query)
    setUiState(uiState)
  }
  return (
    <Container>
      {/* CSS */}
      <Css global={InstantsearchCss.toString()} />
      <InstantSearchCSSOverride />
      {/* Algolia InstantSearch instance */}
      <InstantSearch
        searchClient={searchClient}
        insights={{
          insightsInitParams: {
            userToken: spiceIdHash
          }
        }}
        initialUiState={{
          '': {
            query
          }
        }}
        onStateChange={handleStateChange}>
        <Container
          layout={Container.layouts.mid}
          px={['8', '16', '36']}
          pt={['6', '8', '16']}>
          {/* Search input */}
          <Container>
            <SearchBox
              searchAsYouType={false}
              placeholder={translations.tk_searchTerm}
            />
          </Container>
        </Container>
        <Container pb="10">
          {/* Results container, Tabs + Hits results */}
          <ResultsContainer
            tabsItems={sourcesConfiguration}
            translations={translations}
            recommendedContent={recommendedContent}
            initialQuerySuggestionsHits={initialQuerySuggestionsHits}
          />
        </Container>
      </InstantSearch>
    </Container>
  )
}

// Results container, Tabs + Hits results, as they will depend on scopedResults
const ResultsContainer = ({
  tabsItems,
  translations,
  recommendedContent,
  initialQuerySuggestionsHits
}) => {
  // indexUiState: current query details
  // scopedResults: results of all indexes
  const { indexUiState, scopedResults, status } = useInstantSearch()

  const [initialFetch, setInitialFetch] = useState(false)
  const [selectedTabItemIndex, setSelectedTabItemIndex] = useState(0)
  const [currentQuery, setCurrentQuery] = useState('')

  useEffect(() => {
    const hasResults = resultTabsItems
      .map(el => !!el.nbHits)
      .filter(Boolean).length
    setCurrentQuery(indexUiState?.query)
    if (
      status === 'idle' &&
      (indexUiState?.query !== currentQuery || !initialFetch) &&
      hasResults
    ) {
      const { newSelectedTabItemIndex } = mapResultsToTabsItems(
        tabsItems,
        scopedResults
      )
      setSelectedTabItemIndex(newSelectedTabItemIndex)
      setInitialFetch(true)
      fireGAsearchResultsViewedEvent(newSelectedTabItemIndex)
    }
  }, [scopedResults, status, indexUiState])

  const fireGAsearchResultsViewedEvent = (tabIndex, isLoadMore) => {
    DcxAnalytics.searchResultsViewedEvent(
      indexUiState?.query || '',
      resultTabsItems[tabIndex]?.nbHits || 0,
      resultTabsItems[tabIndex]?.indexId,
      resultTabsItems[tabIndex]?.currentPageNb + (isLoadMore ? 1 : 0)
    )
  }

  const { resultTabsItems } = mapResultsToTabsItems(tabsItems, scopedResults)

  const noResults = !resultTabsItems.map(el => !!el.nbHits).filter(Boolean)
    .length

  return (
    <Container>
      <Container layout={Container.layouts.mid} px={['8', '16', '36']}>
        {currentQuery && (
          <Container pt={['5', '8', '10']}>
            <TextBody
              acceptsHtml
              value={`${translations.tk_searchResultsFor} <b>${currentQuery}</b>`}
            />
          </Container>
        )}
        {noResults && (
          <NoResultsSection
            translations={translations}
            recommendedContent={recommendedContent}
            initialQuerySuggestionsHits={initialQuerySuggestionsHits}
          />
        )}
        <Container pt="8">
          <Tabs
            items={resultTabsItems
              .map((el, index) =>
                el.nbHits
                  ? {
                      index,
                      text: `${el.text} (${el.nbHits})`,
                      nbHits: el.nbHits
                    }
                  : null
              )
              .filter(Boolean)}
            isItemSelected={item => item.index === selectedTabItemIndex}
            getItemLabel={item => item.text}
            onItemSelectedHandler={item => {
              setSelectedTabItemIndex(item.index)
              fireGAsearchResultsViewedEvent(item.index)
            }}
            isScrollable={isMobile().any}
            justifyTabs={
              Tabs.justifyTabsValues[isMobile().any ? 'start' : 'between']
            }
          />
        </Container>
      </Container>
      {!noResults && <Container divider={Container.dividers.bottom} />}
      <Container layout={Container.layouts.mid} px={['8', '16', '36']}>
        {resultTabsItems.map((tabItem, tabIndex) => (
          <Container
            key={tabIndex}
            display={
              selectedTabItemIndex === tabIndex
                ? Container.displays.block
                : Container.displays.none
            }>
            <ResultsTabHits
              {...tabItem}
              translations={translations}
              currentQuery={currentQuery}
              onLoadMore={() => {
                fireGAsearchResultsViewedEvent(selectedTabItemIndex, true)
              }}
            />
          </Container>
        ))}
      </Container>
    </Container>
  )
}

// This component needs to be warraped with memo, to make sure no multiple renders are done as it will cause multiple searchs
const ResultsTabHits = memo(function ResultsHits({
  indexId,
  indexName,
  filters,
  limitSearchPage,
  translations,
  currentPageNb,
  currentQuery,
  onLoadMore
}) {
  let displayType

  switch (indexId) {
    case IndexIdTypes.PRODUCTS_DEVICES:
    case IndexIdTypes.PRODUCTS_CONSUMABLES:
      displayType = DisplayTypes.PRODUCTS
      break
    case IndexIdTypes.ARTICLES_SUPPORT:
    case IndexIdTypes.ARTICLES_NEWS:
      displayType = DisplayTypes.ARTICLES_IMAGE
      break
    case IndexIdTypes.ARTICLES_FAQ:
      displayType = DisplayTypes.ARTICLES_SIMPLIFIED
      break
  }
  return (
    <Container pt={['5', '10']}>
      {/* Result list by indexName and indexId */}
      <Index indexName={indexName} indexId={indexId}>
        <Configure filters={filters} hitsPerPage={limitSearchPage} />
        {/* Result list */}
        <CustomInfiniteHits
          displayType={displayType}
          translations={translations}
          indexId={indexId}
          currentPageNb={currentPageNb}
          currentQuery={currentQuery}
        />
        <LoadMoreButton translations={translations} onLoadMore={onLoadMore} />
      </Index>
    </Container>
  )
})

const CustomInfiniteHits = ({
  displayType,
  translations,
  indexId,
  currentPageNb,
  currentQuery,
  ...props
}) => {
  const { items, sendEvent } = useInfiniteHits(props)

  const renderItems = () =>
    items.map((item, index) => (
      <Hit
        key={index}
        hit={item}
        displayType={displayType}
        translations={translations}
        indexId={indexId}
        currentPageNb={currentPageNb}
        currentQuery={currentQuery}
        // As we are using a custom hook, is required to perform the click sendEvent. https://www.algolia.com/doc/guides/building-search-ui/events/react/
        sendEvent={() => sendEvent('click', item, 'Hit Clicked')}
      />
    ))

  switch (displayType) {
    case DisplayTypes.PRODUCTS:
      return (
        <ListContainer
          columns={[2, 3, 4]}
          xBetween={['2', '4', '10']}
          yBetween={['2', '4', '10']}>
          {renderItems()}
        </ListContainer>
      )
    case DisplayTypes.ARTICLES_IMAGE:
      return (
        <ListContainer
          columns={[1, 2, 3]}
          xBetween={['0', '8', '16']}
          yBetween="10">
          {renderItems()}
        </ListContainer>
      )
    case DisplayTypes.ARTICLES_SIMPLIFIED:
      return (
        <ListContainer columns={[1]} yBetween="10">
          {renderItems()}
        </ListContainer>
      )
  }
}

// Hit component
const Hit = ({
  hit,
  displayType,
  translations,
  sendEvent,
  indexId,
  currentPageNb,
  currentQuery
}) => {
  const fireSearchClickGA = () => {
    DcxAnalytics.searchResultClickEvent(
      currentQuery || '',
      hit.__position,
      hit.title || hit.name,
      indexId,
      currentPageNb
    )
  }
  switch (displayType) {
    case DisplayTypes.PRODUCTS:
      return (
        <ProductCard
          hit={hit}
          fullWidth
          translations={translations}
          sendEvent={sendEvent}
          indexId={indexId}
          currentPageNb={currentPageNb}
          fireSearchClickGA={fireSearchClickGA}
        />
      )
    case DisplayTypes.ARTICLES_IMAGE:
      return (
        <ArticleCard
          hit={hit}
          sendEvent={sendEvent}
          indexId={indexId}
          currentPageNb={currentPageNb}
          fireSearchClickGA={fireSearchClickGA}
        />
      )
    case DisplayTypes.ARTICLES_SIMPLIFIED:
      return (
        <Article
          hit={hit}
          sendEvent={sendEvent}
          indexId={indexId}
          currentPageNb={currentPageNb}
          fireSearchClickGA={fireSearchClickGA}
        />
      )
  }
}

const NoResultsSection = ({
  translations,
  recommendedContent,
  initialQuerySuggestionsHits
}) => {
  return (
    <Container>
      <NoResults translations={translations} />
      <RecommendedSection
        translations={translations}
        recommendedContent={recommendedContent}
        initialQuerySuggestionsHits={initialQuerySuggestionsHits}
      />
    </Container>
  )
}

export default InstantSearchComp
